import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function ReviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // 카테고리 매핑
  const category1Options = [
    { value: 0, label: '서빙로봇' },
    { value: 1, label: '청소로봇' },
    { value: 2, label: '인터뷰' },  // 물류로봇 → 인터뷰로 변경
  ];

  const category2Options = [
    { value: 0, label: '식당' },
    { value: 1, label: '뷔페' },
    { value: 2, label: '카페베이커리' },  // 카페+베이커리 통합
    { value: 3, label: '스크린골프' },
    { value: 4, label: '병원' },
    { value: 5, label: '호텔' },
    { value: 6, label: '기타' },
  ];

  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: '',
    content: '',
    url: '',
    category1: 0,
    category2: 0,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // 인터뷰가 선택되었는지 확인
  const isInterviewSelected = editData.category1 === 2;

  useEffect(() => {
    const fetchReviewDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('API 요청 시작:', `/api/admin/review_d/${id}`);
        
        const response = await axios.get(`/api/admin/review_d/${id}`);
        console.log('API 응답:', response.data);
        
        if (response.data && response.data.success !== false) {
          const data = response.data.data;
          setReviewData(data);
          
          // 수정 폼 초기 데이터 설정
          setEditData({
            title: data.r_title || '',
            content: data.r_content || '',
            url: data.r_url || '',
            category1: data.r_category1 || 0,
            category2: data.r_category2 || 0,
          });
          console.log('데이터 설정됨:', data);
        } else {
          console.error('API 응답 오류:', response.data);
          setError(response.data.error || '정보를 불러오는데 실패했습니다.');
        }
      } catch (error) {
        console.error('API 요청 실패:', error);
        
        let errorMessage = '데이터를 불러오는 중 오류가 발생했습니다.';

        if (error.response) {
          console.error('응답 상태:', error.response.status);
          console.error('응답 데이터:', error.response.data);
          errorMessage = `서버 오류 (${error.response.status}): ${
            error.response.data.message || error.response.statusText
          }`;
        } else if (error.request) {
          console.error('요청 정보:', error.request);
          errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
        } else {
          console.error('오류 메시지:', error.message);
          errorMessage = `요청 오류: ${error.message}`;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchReviewDetail();
    }
  }, [id]);

  // 파일 선택 처리
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        // 5MB
        alert('파일 크기는 5MB를 초과할 수 없습니다.');
        e.target.value = '';
        return;
      }

      setSelectedFile(file);
    }
  };

  // 수정 모드 전환
  const handleEditClick = () => {
    setIsEditing(true);
  };

  // 수정 취소
  const handleCancelEdit = () => {
    setIsEditing(false);

    // 원래 데이터로 복원
    setEditData({
      title: reviewData.r_title || '',
      content: reviewData.r_content || '',
      url: reviewData.r_url || '',
      category1: reviewData.r_category1 || 0,
      category2: reviewData.r_category2 || 0,
    });

    // 파일 선택 초기화
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 입력 필드 변경 처리
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || name === 'category1' || name === 'category2' 
      ? parseInt(value) : value;
    
    setEditData(prevData => {
      const newData = {
        ...prevData,
        [name]: newValue,
      };
      
      // category1이 2(인터뷰)로 변경되면 category2를 0으로 고정
      if (name === 'category1' && newValue === 2) {
        newData.category2 = 0;
      }
      
      return newData;
    });
  };

  const handleSaveEdit = async () => {
    try {
      // 제목이 비어있는지 확인
      if (!editData.title.trim()) {
        alert('제목을 입력해주세요.');
        return;
      }

      // 내용이 비어있는지 확인
      if (!editData.content.trim()) {
        alert('내용을 입력해주세요.');
        return;
      }

      // 인터뷰일 때는 category2를 0으로 강제 설정
      const finalCategory2 = editData.category1 === 2 ? 0 : editData.category2;

      console.log('저장할 데이터:', {
        r_no: id,
        title: editData.title,
        content: editData.content,
        url: editData.url,
        category1: editData.category1,
        category2: finalCategory2,
        hasFile: !!selectedFile
      });

      let response;

      if (selectedFile) {
        // 파일이 있는 경우: FormData 사용
        const formData = new FormData();
        formData.append('r_no', id);
        formData.append('title', editData.title);
        formData.append('content', editData.content);
        formData.append('url', editData.url || '');
        formData.append('category1', editData.category1);
        formData.append('category2', finalCategory2);
        formData.append('file', selectedFile);

        console.log('FormData로 파일 포함 수정 요청');
        response = await axios.post(
          '/api/admin/review/updateWithFile',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        // 파일이 없는 경우: JSON 사용
        const requestData = {
          r_no: id,
          title: editData.title,
          content: editData.content,
          url: editData.url || '',
          category1: editData.category1,
          category2: finalCategory2,
        };

        console.log('JSON으로 일반 수정 요청:', requestData);
        response = await axios.post('/api/admin/review/update', requestData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }

      if (response.data && response.data.success) {
        alert('수정이 완료되었습니다.');

        setIsEditing(false);

        // 최신 데이터 가져오기
        const refreshResponse = await axios.get(`/api/admin/review_d/${id}`);
        if (refreshResponse.data && refreshResponse.data.success !== false) {
          setReviewData(refreshResponse.data.data);
          setEditData({
            title: refreshResponse.data.data.r_title || '',
            content: refreshResponse.data.data.r_content || '',
            url: refreshResponse.data.data.r_url || '',
            category1: refreshResponse.data.data.r_category1 || 0,
            category2: refreshResponse.data.data.r_category2 || 0,
          });
        }

        // 파일 선택 초기화
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        alert(
          '수정에 실패했습니다: ' + (response.data.message || '알 수 없는 오류')
        );
      }
    } catch (error) {
      console.error('수정 요청 실패:', error);
      alert(
        '수정 중 오류가 발생했습니다: ' +
          (error.response?.data?.message || error.message)
      );
    }
  };

  // 파일 확장자 체크 함수
  const isAllowedFileType = (fileName) => {
    if (!fileName) return false;

    const fileExtension = fileName
      .substring(fileName.lastIndexOf('.') + 1)
      .toLowerCase();
    const denyTypes =
      /^(exe|jsp|jspx|jsw|jsv|jspf|phtml|php|php3|php4|php5|inc|js|html|htm)$/;

    return !denyTypes.test(fileExtension);
  };

  // 첨부파일 렌더링
  const renderAttachment = () => {
    if (
      !reviewData ||
      !reviewData.r_file1_s ||
      !isAllowedFileType(reviewData.r_file1_s)
    ) {
      return <p>첨부파일 없음</p>;
    }

    // 이미지 URL을 스프링 서버 경로로 변경
    const filePath = `/resources/upload/${reviewData.r_file1_s}`;

    return (
      <div>
        <a
          href={filePath}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: '#007bff' }}
        >
          📎 {reviewData.r_file1_o}
        </a>
      </div>
    );
  };

  // 카테고리 텍스트 반환
  const getCategory1Text = (category) => {
    const found = category1Options.find((opt) => opt.value === category);
    return found ? found.label : '알 수 없음';
  };

  const getCategory2Text = (category) => {
    const found = category2Options.find((opt) => opt.value === category);
    return found ? found.label : '알 수 없음';
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('날짜 포맷팅 오류:', error);
      return dateString;
    }
  };

  const handleGoToList = () => {
    navigate('/admin/review');
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="titleNav">설치사례</div>
        <div className="wrap">
          <div
            className="over_hi"
            style={{ marginTop: '60px', textAlign: 'center' }}
          >
            <p>로딩 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-wrapper">
        <div className="titleNav">설치사례</div>
        <div className="wrap">
          <div
            className="over_hi"
            style={{ marginTop: '60px', textAlign: 'center' }}
          >
            <p style={{ color: 'red' }}>{error}</p>
            <button
              type="button"
              className="common-del"
              onClick={handleGoToList}
            >
              목록으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!reviewData) {
    return (
      <div className="content-wrapper">
        <div className="titleNav">설치사례</div>
        <div className="wrap">
          <div
            className="over_hi"
            style={{ marginTop: '60px', textAlign: 'center' }}
          >
            <p>설치사례를 찾을 수 없습니다.</p>
            <button
              type="button"
              className="common-del"
              onClick={handleGoToList}
            >
              목록으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="content-wrapper">
        <div className="titleNav">설치사례</div>
        {/* content S */}
        <div className="">
          <div className="over_hi" style={{ marginTop: '60px' }}>
            <form id="updateFrm" method="post" action="">
              <table className="adminTable">
                <tbody>
                  <tr>
                    <th style={{ width: '200px' }}>카테고리</th>
                    <td className="left">
                      {isEditing ? (
                        <div
                          style={{
                            display: 'flex',
                            gap: '15px',
                            flexWrap: 'wrap',
                          }}
                        >
                          {category1Options.map((option) => (
                            <label
                              key={option.value}
                              style={{ marginRight: '20px' }}
                            >
                              <input
                                type="radio"
                                name="category1"
                                value={option.value}
                                checked={editData.category1 === option.value}
                                onChange={handleInputChange}
                                style={{ marginRight: '5px' }}
                              />
                              {option.label}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p>{getCategory1Text(reviewData?.r_category1)}</p>
                      )}
                    </td>
                  </tr>
                  
                  {/* 인터뷰가 아닐 때만 업종 표시 */}
                  {((!isEditing && reviewData?.r_category1 !== 2) || (isEditing && !isInterviewSelected)) && (
                    <tr>
                      <th style={{ width: '200px' }}>업종</th>
                      <td className="left">
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                            {category2Options.map((option) => (
                              <label
                                key={option.value}
                                style={{ marginRight: '20px' }}
                              >
                                <input
                                  type="radio"
                                  name="category2"
                                  value={option.value}
                                  checked={editData.category2 === option.value}
                                  onChange={handleInputChange}
                                  style={{ marginRight: '5px' }}
                                />
                                {option.label}
                              </label>
                            ))}
                          </div>
                        ) : (
                          <p>{getCategory2Text(reviewData?.r_category2)}</p>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* 인터뷰 수정 시 안내 메시지 */}
                  {isEditing && isInterviewSelected && (
                    <tr>
                      <th style={{ width: '200px' }}>업종</th>
                      <td className="left">
                        <div style={{ 
                          padding: '10px', 
                          backgroundColor: '#f8f9fa', 
                          border: '1px solid #dee2e6', 
                          borderRadius: '4px',
                          color: '#6c757d'
                        }}>
                          인터뷰 카테고리는 별도의 업종 분류가 없습니다.
                        </div>
                      </td>
                    </tr>
                  )}
                  
                  <tr>
                    <th style={{ width: '200px' }}>제목</th>
                    <td className="left">
                      {isEditing ? (
                        <input
                          type="text"
                          name="title"
                          value={editData.title}
                          onChange={handleInputChange}
                          style={{ width: '100%', padding: '8px' }}
                          placeholder="설치사례 제목을 입력하세요"
                        />
                      ) : (
                        <p>{reviewData?.r_title || '-'}</p>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th style={{ width: '200px' }}>내용</th>
                    <td className="left">
                      {isEditing ? (
                        <textarea
                          name="content"
                          value={editData.content}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            height: '300px',
                            padding: '8px',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                          }}
                          placeholder="설치사례 내용을 입력하세요"
                        />
                      ) : (
                        <div
                          style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}
                        >
                          {reviewData?.r_content || '-'}
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th style={{ width: '200px' }}>관련 URL</th>
                    <td className="left">
                      {isEditing ? (
                        <input
                          type="url"
                          name="url"
                          value={editData.url}
                          onChange={handleInputChange}
                          style={{ width: '100%', padding: '8px' }}
                          placeholder="https://example.com (선택사항)"
                        />
                      ) : reviewData?.r_url ? (
                        <a
                          href={reviewData.r_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#007bff', textDecoration: 'none' }}
                        >
                          {reviewData.r_url}
                        </a>
                      ) : (
                        <p>-</p>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th style={{ width: '200px' }}>
                      첨부파일
                      <br />
                      (~5MB)
                    </th>
                    <td className="left" style={{ lineHeight: '1.6' }}>
                      {isEditing ? (
                        <div>
                          <input
                            type="file"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            style={{ marginBottom: '10px' }}
                            accept="image/*,.pdf,.doc,.docx,.hwp,.txt"
                          />
                          <p style={{ fontSize: '14px', color: '#666' }}>
                            현재 파일: {reviewData?.r_file1_o || '없음'}
                            {selectedFile && (
                              <span
                                style={{ color: '#4CAF50', marginLeft: '10px' }}
                              >
                                새 파일: {selectedFile.name}
                              </span>
                            )}
                          </p>
                        </div>
                      ) : (
                        renderAttachment()
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th style={{ width: '200px' }}>등록일시</th>
                    <td className="left">
                      <p>{formatDate(reviewData?.r_add_dt)}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </form>

            <div className="right">
              {isEditing ? (
                <>
                  <input
                    type="button"
                    value="저장"
                    className="common-del"
                    onClick={handleSaveEdit}
                    style={{ marginRight: '10px' }}
                  />
                  <input
                    type="button"
                    value="취소"
                    className="common-del"
                    onClick={handleCancelEdit}
                  />
                </>
              ) : (
                <>
                  <input
                    type="button"
                    value="수정하기"
                    className="common-del"
                    onClick={handleEditClick}
                    style={{ marginRight: '10px' }}
                  />

                  <input
                    type="button"
                    value="목록으로"
                    className="common-del"
                    onClick={handleGoToList}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}